import { clsx } from 'clsx';
import { assignInlineVars } from '@vanilla-extract/dynamic';
import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Typography } from '../typography/typography';
import { InputLabel } from './input';
import { controlWrapper, errorText, supportingText } from './input.css';
import {
  control,
  controlDisabled,
  controlError,
  cursor,
  hiddenInput,
  placeholderDot,
  slot,
  slotCountVar,
  slots,
  valueText,
} from './confirmationCodeInput.css';

const DEFAULT_MASK = /[0-9]/;
const DEFAULT_LENGTH = 6;
const CONTROL_KEYS = new Set([
  'Backspace',
  'Delete',
  'Tab',
  'ArrowLeft',
  'ArrowRight',
  'ArrowUp',
  'ArrowDown',
  'Home',
  'End',
  'Enter',
  'Escape',
]);

const toMaskWithoutGlobalFlag = (mask: RegExp): RegExp => {
  const flags = mask.flags.replace('g', '');
  return new RegExp(mask.source, flags);
};

const toCodeLength = (value: number | undefined): number => {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return DEFAULT_LENGTH;
  }

  return Math.max(1, Math.floor(value));
};

const filterWithMask = (
  value: string,
  mask: RegExp,
  length: number,
): string => {
  const normalizedMask = toMaskWithoutGlobalFlag(mask);
  const allowedChars = Array.from(value).filter((character) =>
    normalizedMask.test(character),
  );

  return allowedChars.join('').slice(0, length);
};

const isAllowedCharacter = (character: string, mask: RegExp): boolean => {
  const normalizedMask = toMaskWithoutGlobalFlag(mask);
  return normalizedMask.test(character);
};

type ConfirmationCodeInputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'onChange' | 'value' | 'type'
> & {
  value: string;
  onChange: (value: string) => void;
  onComplete?: (value: string) => void;
  mask?: RegExp;
  length?: number;
  label?: string;
  error?: string;
  supportingText?: string;
  hideError?: boolean;
  testId?: string;
  wrapperClassName?: string;
  controlClassName?: string;
};

export const ConfirmationCodeInput = ({
  value,
  onChange,
  onComplete,
  mask = DEFAULT_MASK,
  length,
  label,
  error,
  supportingText: supportText,
  hideError,
  testId,
  wrapperClassName,
  controlClassName,
  disabled,
  required,
  autoComplete,
  inputMode,
  pattern,
  'aria-label': ariaLabel,
  onKeyDown,
  onPaste,
  onFocus,
  onBlur,
  ...inputProps
}: ConfirmationCodeInputProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const shouldRestoreFocusRef = useRef(false);
  const generatedInputId = useId();
  const inputId = (
    inputProps.id ?? `confirmation-code-input-${generatedInputId}`
  ).replaceAll(':', '');
  const codeLength = toCodeLength(length);
  const resolvedAriaLabel = ariaLabel ?? label ?? 'Confirmation code';
  const hasDefaultNumericMask = mask.source === DEFAULT_MASK.source;
  const sanitizedValue = useMemo(
    () => filterWithMask(value, mask, codeLength),
    [codeLength, mask, value],
  );
  const activeSlotIndex = isFocused
    ? Math.min(sanitizedValue.length, codeLength - 1)
    : -1;

  const setNextValue = (nextRawValue: string): void => {
    const nextValue = filterWithMask(nextRawValue, mask, codeLength);

    if (nextValue !== sanitizedValue) {
      onChange(nextValue);
    }

    if (
      nextValue.length === codeLength &&
      sanitizedValue.length !== codeLength
    ) {
      onComplete?.(nextValue);
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setNextValue(event.currentTarget.value);
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>): void => {
    onPaste?.(event);

    if (event.defaultPrevented || disabled) {
      return;
    }

    event.preventDefault();
    setNextValue(event.clipboardData.getData('text'));
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ): void => {
    onKeyDown?.(event);

    if (event.defaultPrevented || disabled) {
      return;
    }

    if (event.ctrlKey || event.metaKey || event.altKey) {
      return;
    }

    if (CONTROL_KEYS.has(event.key)) {
      return;
    }

    if (event.key.length !== 1 || !isAllowedCharacter(event.key, mask)) {
      event.preventDefault();
      return;
    }

    if (sanitizedValue.length >= codeLength) {
      event.preventDefault();
    }
  };

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>): void => {
    setIsFocused(true);
    onFocus?.(event);
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>): void => {
    setIsFocused(false);
    onBlur?.(event);
  };

  const focusInput = (): void => {
    if (disabled) {
      return;
    }

    const inputElement = inputRef.current;
    if (!inputElement) {
      return;
    }

    inputElement.focus();
    const cursorPosition = sanitizedValue.length;
    inputElement.setSelectionRange(cursorPosition, cursorPosition);
  };

  const handleControlMouseDown = (
    event: React.MouseEvent<HTMLDivElement>,
  ): void => {
    if (disabled) {
      return;
    }

    // Make the whole visual control re-focusable after error/blur.
    event.preventDefault();
    focusInput();
  };

  useEffect(() => {
    if (disabled) {
      const inputElement = inputRef.current;
      shouldRestoreFocusRef.current =
        inputElement !== null &&
        (document.activeElement === inputElement || isFocused);
      setIsFocused(false);
      return;
    }

    if (!shouldRestoreFocusRef.current) {
      return;
    }

    shouldRestoreFocusRef.current = false;
    focusInput();
    setIsFocused(true);
  }, [disabled, isFocused]);

  return (
    <div
      className={clsx(controlWrapper, wrapperClassName)}
      data-testid={testId}
    >
      {label ? (
        <InputLabel
          disabled={disabled}
          error={error}
          required={required}
          htmlFor={inputId}
        >
          {label}
        </InputLabel>
      ) : null}

      <div
        className={clsx(
          control,
          error && controlError,
          disabled && controlDisabled,
          controlClassName,
        )}
        data-testid={testId ? `${testId}-control` : undefined}
        onMouseDown={handleControlMouseDown}
      >
        <input
          {...inputProps}
          ref={inputRef}
          id={inputId}
          aria-label={resolvedAriaLabel}
          type="text"
          value={sanitizedValue}
          disabled={disabled}
          required={required}
          className={hiddenInput}
          data-testid={testId ? `${testId}-input` : undefined}
          autoComplete={autoComplete ?? 'one-time-code'}
          inputMode={inputMode ?? (hasDefaultNumericMask ? 'numeric' : 'text')}
          pattern={pattern ?? (hasDefaultNumericMask ? '[0-9]*' : undefined)}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onFocus={handleFocus}
          onBlur={handleBlur}
        />

        <div
          className={slots}
          style={assignInlineVars({
            [slotCountVar]: `${codeLength}`,
          })}
        >
          {Array.from({ length: codeLength }).map((_, index) => {
            const character = sanitizedValue[index] ?? '';
            const showCursor =
              activeSlotIndex === index && sanitizedValue.length < codeLength;

            return (
              <div key={`${index}`} className={slot} aria-hidden>
                {character ? (
                  <span className={valueText}>{character}</span>
                ) : null}
                {!character ? <span className={placeholderDot}>•</span> : null}
                {!character && showCursor ? <span className={cursor} /> : null}
              </div>
            );
          })}
        </div>
      </div>

      {(supportText || error) && !hideError ? (
        <Typography
          variant="body"
          size="small"
          className={clsx(supportingText, error && errorText)}
        >
          {error || supportText}
        </Typography>
      ) : null}
    </div>
  );
};
